import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

import type {
  NativeBookingApi,
  NativeFormDocumentRequirement,
  NativeFormField,
  NativeSuggestion,
} from './native-booking-api';
import { documentIdentityKey } from './native-booking-guards';
import {
  documentMetadataPlateSelectionKey,
  readDocumentMetaPlateSelection,
  readNativePlateFormatChoices,
} from './native-plate-format-choice';
import {
  applyRuPlateMask,
  isRuPlateSelectableInputFormat,
  readRuPlateInputFormat,
  RU_PLATE_FORMAT_CODES,
  RU_PLATE_FORMAT_SPECS,
  RU_PLATE_RUNTIME_NONE,
  ruPlateMaxLength,
  ruPlatePlaceholder,
  ruPlateUserFacingError,
  tryCanonicalizeRuPlate,
  type RuPlateFormatCode,
  type RuPlateRuntimeSelection,
} from './ru-plate-formats';

function draftValue(draft: Record<string, unknown>, key: string): string {
  const raw = draft[key];
  if (raw === null || raw === undefined) {
    return '';
  }
  return String(raw);
}

function docDraftValue(
  draft: Record<string, unknown>,
  documentTypeId: string,
  subjectBinding: string,
  fieldCode: string,
): string {
  const docs = draft.documents;
  if (typeof docs !== 'object' || docs === null || Array.isArray(docs)) {
    return '';
  }
  const identity = documentIdentityKey(documentTypeId, subjectBinding);
  const row = (docs as Record<string, unknown>)[identity];
  if (typeof row !== 'object' || row === null || Array.isArray(row)) {
    return '';
  }
  const metadata = (row as Record<string, unknown>).metadata;
  if (typeof metadata !== 'object' || metadata === null || Array.isArray(metadata)) {
    return '';
  }
  const val = (metadata as Record<string, unknown>)[fieldCode];
  return val === null || val === undefined ? '' : String(val);
}

/** True when the field contract expects a JSON number (not a digit string). */
export function isNumericValueType(valueType: string): boolean {
  const t = valueType.toUpperCase();
  if (t.includes('PHONE') || t.includes('TEL') || t.includes('EMAIL')) {
    return false;
  }
  return t.includes('INTEGER') || t.includes('YEAR') || t.includes('NUMBER') || t.includes('DECIMAL');
}

/**
 * Coerce a UI string into the JSON value expected by the field value_type.
 * Empty → null. Invalid numeric text is left as the original string (no silent 0/NaN).
 * STRING/TEXT/phone/registration keep string form (leading zeros preserved).
 */
export function coerceTypedDraftValue(valueType: string, raw: string): unknown {
  if (raw.trim() === '') {
    return null;
  }
  const t = valueType.toUpperCase();
  if (t.includes('PHONE') || t.includes('TEL') || t.includes('EMAIL') || t.includes('DATE')) {
    return raw;
  }
  if (t.includes('INTEGER') || t.includes('YEAR')) {
    if (!/^-?\d+$/.test(raw.trim())) {
      return raw;
    }
    const n = Number(raw.trim());
    if (!Number.isSafeInteger(n)) {
      return raw;
    }
    return n;
  }
  if (t.includes('NUMBER') || t.includes('DECIMAL') || t.includes('FLOAT')) {
    const trimmed = raw.trim();
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
      return raw;
    }
    const n = Number(trimmed);
    if (!Number.isFinite(n)) {
      return raw;
    }
    return n;
  }
  return raw;
}

export function buildDraftPatch(
  previous: Record<string, unknown>,
  fieldKey: string,
  value: string,
  valueType: string,
): Record<string, unknown> {
  return { ...previous, [fieldKey]: coerceTypedDraftValue(valueType, value) };
}

/** Writes `draft.documents[typeId::subject] = { document_type_id, subject_binding, metadata }`. */
export function buildDocumentDraftPatch(
  previous: Record<string, unknown>,
  documentTypeId: string,
  subjectBinding: string,
  fieldCode: string,
  value: string,
  valueType: string,
): Record<string, unknown> {
  const identity = documentIdentityKey(documentTypeId, subjectBinding);
  const docsRaw = previous.documents;
  const docs =
    typeof docsRaw === 'object' && docsRaw !== null && !Array.isArray(docsRaw)
      ? { ...(docsRaw as Record<string, unknown>) }
      : {};
  const rowRaw = docs[identity];
  const row =
    typeof rowRaw === 'object' && rowRaw !== null && !Array.isArray(rowRaw)
      ? { ...(rowRaw as Record<string, unknown>) }
      : {
          document_type_id: documentTypeId,
          subject_binding: subjectBinding,
          metadata: {},
        };
  const metadataRaw = row.metadata;
  const metadata =
    typeof metadataRaw === 'object' && metadataRaw !== null && !Array.isArray(metadataRaw)
      ? { ...(metadataRaw as Record<string, unknown>) }
      : {};
  metadata[fieldCode] = coerceTypedDraftValue(valueType, value);
  docs[identity] = {
    document_type_id: documentTypeId,
    subject_binding: subjectBinding,
    metadata,
  };
  return { ...previous, documents: docs };
}

/** Normalize persisted draft strings for numeric fields before PATCH/submit/apply. */
export function normalizeDraftTypes(
  draft: Record<string, unknown>,
  fields: readonly NativeFormField[],
  documents: readonly NativeFormDocumentRequirement[],
): Record<string, unknown> {
  let next: Record<string, unknown> = { ...draft };
  for (const field of fields) {
    if (!(field.field_key in next)) {
      continue;
    }
    const current = next[field.field_key];
    if (typeof current === 'string') {
      next = { ...next, [field.field_key]: coerceTypedDraftValue(field.value_type, current) };
    }
  }
  const docsRaw = next.documents;
  if (typeof docsRaw !== 'object' || docsRaw === null || Array.isArray(docsRaw)) {
    return next;
  }
  const docs = { ...(docsRaw as Record<string, unknown>) };
  let docsChanged = false;
  for (const doc of documents) {
    const identity = documentIdentityKey(doc.document_type_id, doc.subject_binding);
    const rowRaw = docs[identity];
    if (typeof rowRaw !== 'object' || rowRaw === null || Array.isArray(rowRaw)) {
      continue;
    }
    const row = { ...(rowRaw as Record<string, unknown>) };
    const metadataRaw = row.metadata;
    if (typeof metadataRaw !== 'object' || metadataRaw === null || Array.isArray(metadataRaw)) {
      continue;
    }
    const metadata = { ...(metadataRaw as Record<string, unknown>) };
    let metaChanged = false;
    for (const meta of doc.metadata_fields) {
      const current = metadata[meta.field_code];
      if (typeof current !== 'string') {
        continue;
      }
      const coerced = coerceTypedDraftValue(meta.value_type, current);
      if (coerced !== current) {
        metadata[meta.field_code] = coerced;
        metaChanged = true;
      }
    }
    if (metaChanged) {
      docs[identity] = {
        ...row,
        document_type_id: doc.document_type_id,
        subject_binding: doc.subject_binding,
        metadata,
      };
      docsChanged = true;
    }
  }
  return docsChanged ? { ...next, documents: docs } : next;
}

function RuPlateInput(props: {
  readonly format: RuPlateFormatCode;
  readonly value: string;
  readonly disabled: boolean;
  readonly readOnly?: boolean;
  readonly invalid?: boolean;
  readonly fieldKey: string;
  readonly onChange: (value: string) => void;
}): ReactElement {
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const composingRef = useRef(false);
  const caretRef = useRef(0);
  const spec = RU_PLATE_FORMAT_SPECS[props.format];

  useEffect(() => {
    const el = inputRef.current;
    if (el === null || document.activeElement !== el) {
      return;
    }
    const next = Math.max(0, Math.min(caretRef.current, props.value.length));
    el.setSelectionRange(next, next);
  }, [props.value]);

  return (
    <>
      <input
        ref={inputRef}
        className={props.invalid || localError !== null ? 'native-input is-invalid' : 'native-input'}
        type="text"
        disabled={props.disabled}
        readOnly={props.readOnly === true}
        value={props.value}
        autoComplete="off"
        placeholder={ruPlatePlaceholder(props.format)}
        inputMode="text"
        maxLength={ruPlateMaxLength(props.format) + 2}
        spellCheck={false}
        data-native-field={props.fieldKey}
        data-ru-plate-mask={props.format}
        aria-invalid={props.invalid || localError !== null}
        aria-describedby={`${props.fieldKey}-ru-plate-hint`}
        onCompositionStart={() => {
          composingRef.current = true;
        }}
        onCompositionEnd={(event) => {
          composingRef.current = false;
          setLocalError(null);
          const previousCaret = event.currentTarget.selectionStart ?? props.value.length;
          const masked = applyRuPlateMask(
            props.format,
            event.currentTarget.value,
            props.value,
            previousCaret,
          );
          caretRef.current = masked.caret;
          props.onChange(masked.value);
        }}
        onChange={(event) => {
          setLocalError(null);
          if (composingRef.current) {
            // Keep IME draft visible without applying mask mid-composition.
            props.onChange(event.target.value);
            return;
          }
          const previousCaret = event.target.selectionStart ?? props.value.length;
          const masked = applyRuPlateMask(
            props.format,
            event.target.value,
            props.value,
            previousCaret,
          );
          caretRef.current = masked.caret;
          props.onChange(masked.value);
        }}
        onBlur={() => {
          if (props.value.trim() === '') {
            setLocalError(null);
            return;
          }
          const result = tryCanonicalizeRuPlate(props.format, props.value);
          if (!result.ok) {
            setLocalError(ruPlateUserFacingError(props.format));
            return;
          }
          setLocalError(null);
          if (result.canonical !== props.value) {
            caretRef.current = result.canonical.length;
            props.onChange(result.canonical);
          }
        }}
      />
      <span id={`${props.fieldKey}-ru-plate-hint`} className="meta">
        Формат: {spec.example}. {spec.hint} Неполный черновик можно сохранить.
      </span>
      {localError !== null ? (
        <span className="native-field-error" role="alert">
          {localError}
        </span>
      ) : null}
    </>
  );
}

function fieldRuPlateFormat(field: {
  readonly validation: Record<string, unknown>;
}): RuPlateFormatCode | null {
  return readRuPlateInputFormat(field.validation);
}

function fieldIsRuPlateSelectable(field: {
  readonly validation: Record<string, unknown>;
}): boolean {
  return isRuPlateSelectableInputFormat(field.validation);
}

function PlateTypeSelect(props: {
  readonly fieldKey: string;
  readonly value: RuPlateRuntimeSelection | '';
  readonly disabled: boolean;
  readonly onChange: (next: RuPlateRuntimeSelection) => void;
}): ReactElement {
  return (
    <select
      className="native-input"
      data-testid={`native-plate-type-${props.fieldKey}`}
      data-native-plate-type={props.fieldKey}
      disabled={props.disabled}
      value={props.value}
      aria-label="Тип регистрационного знака"
      onChange={(event) => {
        const raw = event.target.value;
        if (raw === RU_PLATE_RUNTIME_NONE || (RU_PLATE_FORMAT_CODES as readonly string[]).includes(raw)) {
          props.onChange(raw as RuPlateRuntimeSelection);
        }
      }}
    >
      <option value="" disabled>
        — Выберите тип знака —
      </option>
      {RU_PLATE_FORMAT_CODES.map((code) => (
        <option key={code} value={code}>
          {RU_PLATE_FORMAT_SPECS[code].label}
        </option>
      ))}
      <option value={RU_PLATE_RUNTIME_NONE}>Иной / без маски</option>
    </select>
  );
}

function inputTypeForValueType(valueType: string): string {
  const t = valueType.toUpperCase();
  if (t.includes('EMAIL')) return 'email';
  if (t.includes('PHONE') || t.includes('TEL')) return 'tel';
  if (t.includes('DATE')) return 'date';
  if (t.includes('NUMBER') || t.includes('INTEGER') || t.includes('YEAR') || t.includes('DECIMAL')) {
    return 'number';
  }
  return 'text';
}

/**
 * Same effective key the server uses in saveSuggestions:
 * semanticKey ?? snapshotPath ?? fieldKey.
 */
export function suggestionLookupKey(field: {
  readonly field_key: string;
  readonly semantic_key?: string | null;
  readonly snapshot_path?: string | null;
}): string | null {
  for (const candidate of [field.semantic_key, field.snapshot_path, field.field_key]) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate.trim();
    }
  }
  return null;
}

/** Suggestions are stored only for STRING/TEXT contractor fields (server saveSuggestions). */
export function fieldAcceptsTextSuggestions(field: {
  readonly value_type: string;
  readonly field_key: string;
  readonly semantic_key?: string | null;
  readonly snapshot_path?: string | null;
}): boolean {
  const t = field.value_type.toUpperCase();
  if (!(t.includes('STRING') || t.includes('TEXT'))) {
    return false;
  }
  if (t.includes('PHONE') || t.includes('TEL') || t.includes('EMAIL') || t.includes('DATE')) {
    return false;
  }
  return suggestionLookupKey(field) !== null;
}

function isRegistrationSemantic(key: string | null | undefined): boolean {
  if (key === null || key === undefined) return false;
  const k = key.toLowerCase();
  return (
    k.includes('reg') ||
    k.includes('plate') ||
    k.includes('gos') ||
    k.includes('номер') ||
    k.includes('serial')
  );
}

function isModelSemantic(key: string | null | undefined): boolean {
  if (key === null || key === undefined) return false;
  const k = key.toLowerCase();
  return k.includes('model') || k.includes('mark') || k.includes('brand') || k.includes('make');
}

/** Split «марка/модель — номер» vehicle suggestion labels. */
export function splitVehicleSuggestion(value: string): { model: string; plate: string } | null {
  const parts = value.split(/\s+[—–-]\s+/);
  if (parts.length !== 2) {
    return null;
  }
  const model = parts[0]!.trim();
  const plate = parts[1]!.trim();
  if (model === '' || plate === '') {
    return null;
  }
  return { model, plate };
}

function SuggestionField(props: {
  readonly field: NativeFormField;
  readonly value: string;
  readonly disabled: boolean;
  readonly readOnly?: boolean;
  readonly invalid?: boolean;
  readonly api: NativeBookingApi | null;
  readonly allFields: readonly NativeFormField[];
  readonly onChange: (fieldKey: string, value: string) => void;
  readonly onMultiChange: (patch: Record<string, string>) => void;
  /** When set, keep selected RU plate mask/canonicalize — suggestions must not bypass format. */
  readonly ruPlateFormat?: RuPlateFormatCode | null;
}): ReactElement {
  const [suggestions, setSuggestions] = useState<readonly NativeSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  /** Set only by deliberate input; null means no search (prefill / pick / blur). */
  const [typedQuery, setTypedQuery] = useState<string | null>(null);
  const [localPlateError, setLocalPlateError] = useState<string | null>(null);
  const focusedRef = useRef(false);
  const requestSeq = useRef(0);
  /** Pending blur cleanup — must be cancelled on re-focus or a quick retype is wiped. */
  const blurTimerRef = useRef<number | null>(null);
  const composingRef = useRef(false);
  const caretRef = useRef(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const lookupKey = suggestionLookupKey(props.field);
  const interactive = !props.disabled && props.readOnly !== true;
  const ruPlateFormat = props.ruPlateFormat ?? null;
  const ruPlate = ruPlateFormat !== null;

  useEffect(() => {
    if (!ruPlate) {
      return;
    }
    const el = inputRef.current;
    if (el === null || document.activeElement !== el) {
      return;
    }
    const next = Math.max(0, Math.min(caretRef.current, props.value.length));
    el.setSelectionRange(next, next);
  }, [props.value, ruPlate]);

  useEffect(() => {
    return () => {
      if (blurTimerRef.current !== null) {
        window.clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!interactive) {
      focusedRef.current = false;
      if (blurTimerRef.current !== null) {
        window.clearTimeout(blurTimerRef.current);
        blurTimerRef.current = null;
      }
      setTypedQuery(null);
      setSuggestions([]);
      setOpen(false);
      requestSeq.current += 1;
      return;
    }
    if (props.api === null || lookupKey === null || lookupKey === '' || typedQuery === null) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const q = typedQuery.trim();
    if (q.length < 1) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    const seq = ++requestSeq.current;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void props.api!.listSuggestions(lookupKey, q).then(
        (items) => {
          if (cancelled || seq !== requestSeq.current || !focusedRef.current) {
            return;
          }
          setSuggestions(items);
          setOpen(items.length > 0);
        },
        () => {
          if (cancelled || seq !== requestSeq.current) {
            return;
          }
          setSuggestions([]);
          setOpen(false);
        },
      );
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [props.api, lookupKey, interactive, typedQuery]);

  const maskRuPlateValue = (format: RuPlateFormatCode, raw: string): string => {
    const masked = applyRuPlateMask(format, raw, '', 0).value;
    const canon = tryCanonicalizeRuPlate(format, masked);
    return canon.ok ? canon.canonical : masked;
  };

  const pick = async (item: NativeSuggestion) => {
    if (!interactive) {
      return;
    }
    // Programmatic value update must not re-query or reopen until the next input.
    setTypedQuery(null);
    setSuggestions([]);
    setOpen(false);
    setLocalPlateError(null);
    requestSeq.current += 1;
    const vehicle = splitVehicleSuggestion(item.value);
    if (vehicle !== null && (isModelSemantic(lookupKey) || isRegistrationSemantic(lookupKey))) {
      const patch: Record<string, string> = {};
      const modelField = props.allFields.find((f) => isModelSemantic(suggestionLookupKey(f)));
      const plateField = props.allFields.find((f) =>
        isRegistrationSemantic(suggestionLookupKey(f)),
      );
      if (modelField !== undefined) {
        patch[modelField.field_key] = vehicle.model;
      }
      if (plateField !== undefined) {
        const plateFormat = fieldRuPlateFormat(plateField);
        patch[plateField.field_key] =
          plateFormat !== null ? maskRuPlateValue(plateFormat, vehicle.plate) : vehicle.plate;
      }
      if (Object.keys(patch).length > 0) {
        props.onMultiChange(patch);
      } else {
        props.onChange(
          props.field.field_key,
          ruPlateFormat !== null ? maskRuPlateValue(ruPlateFormat, item.value) : item.value,
        );
      }
    } else {
      props.onChange(
        props.field.field_key,
        ruPlateFormat !== null ? maskRuPlateValue(ruPlateFormat, item.value) : item.value,
      );
    }
  };

  const hide = async (item: NativeSuggestion) => {
    if (!interactive || props.api === null) {
      return;
    }
    try {
      await props.api.hideSuggestion(item.id);
      setSuggestions((prev) => prev.filter((row) => row.id !== item.id));
    } catch {
      // hide is best-effort; keep form usable
    }
  };

  const invalid = props.invalid === true || localPlateError !== null;
  return (
    <div className="native-suggest-wrap">
      <input
        ref={inputRef}
        className={invalid ? 'native-input is-invalid' : 'native-input'}
        type={inputTypeForValueType(props.field.value_type)}
        disabled={props.disabled}
        readOnly={props.readOnly === true}
        value={props.value}
        autoComplete="off"
        placeholder={ruPlateFormat !== null ? ruPlatePlaceholder(ruPlateFormat) : undefined}
        inputMode="text"
        maxLength={ruPlateFormat !== null ? ruPlateMaxLength(ruPlateFormat) + 2 : undefined}
        spellCheck={ruPlate ? false : undefined}
        data-native-field={props.field.field_key}
        data-ru-plate-mask={ruPlateFormat ?? undefined}
        aria-invalid={invalid}
        aria-describedby={ruPlate ? `${props.field.field_key}-ru-plate-hint` : undefined}
        onCompositionStart={() => {
          if (ruPlate) {
            composingRef.current = true;
          }
        }}
        onCompositionEnd={(event) => {
          if (ruPlateFormat === null || !interactive) {
            return;
          }
          composingRef.current = false;
          setLocalPlateError(null);
          const previousCaret = event.currentTarget.selectionStart ?? props.value.length;
          const masked = applyRuPlateMask(
            ruPlateFormat,
            event.currentTarget.value,
            props.value,
            previousCaret,
          );
          caretRef.current = masked.caret;
          setTypedQuery(masked.value);
          props.onChange(props.field.field_key, masked.value);
        }}
        onChange={(event) => {
          if (!interactive) {
            return;
          }
          setLocalPlateError(null);
          if (ruPlateFormat !== null) {
            if (composingRef.current) {
              setTypedQuery(event.target.value);
              props.onChange(props.field.field_key, event.target.value);
              return;
            }
            const previousCaret = event.target.selectionStart ?? props.value.length;
            const masked = applyRuPlateMask(
              ruPlateFormat,
              event.target.value,
              props.value,
              previousCaret,
            );
            caretRef.current = masked.caret;
            setTypedQuery(masked.value);
            props.onChange(props.field.field_key, masked.value);
            return;
          }
          setTypedQuery(event.target.value);
          props.onChange(props.field.field_key, event.target.value);
        }}
        onFocus={() => {
          focusedRef.current = true;
          // Cancel deferred blur cleanup so a rapid re-focus/type keeps typedQuery.
          if (blurTimerRef.current !== null) {
            window.clearTimeout(blurTimerRef.current);
            blurTimerRef.current = null;
          }
          // Prefill/focus alone must not open; only a prior typedQuery search may.
        }}
        onBlur={() => {
          focusedRef.current = false;
          if (ruPlateFormat !== null && props.value.trim() !== '') {
            const result = tryCanonicalizeRuPlate(ruPlateFormat, props.value);
            if (!result.ok) {
              setLocalPlateError(ruPlateUserFacingError(ruPlateFormat));
            } else {
              setLocalPlateError(null);
              if (result.canonical !== props.value) {
                caretRef.current = result.canonical.length;
                props.onChange(props.field.field_key, result.canonical);
              }
            }
          }
          if (blurTimerRef.current !== null) {
            window.clearTimeout(blurTimerRef.current);
          }
          blurTimerRef.current = window.setTimeout(() => {
            blurTimerRef.current = null;
            setOpen(false);
            setTypedQuery(null);
            requestSeq.current += 1;
          }, 180);
        }}
      />
      {ruPlateFormat !== null ? (
        <span id={`${props.field.field_key}-ru-plate-hint`} className="meta">
          Формат: {RU_PLATE_FORMAT_SPECS[ruPlateFormat].example}.{' '}
          {RU_PLATE_FORMAT_SPECS[ruPlateFormat].hint} Неполный черновик можно сохранить.
        </span>
      ) : null}
      {localPlateError !== null ? (
        <span className="native-field-error" role="alert">
          {localPlateError}
        </span>
      ) : null}
      {interactive && open && suggestions.length > 0 ? (
        <ul className="native-suggest-list" role="listbox">
          {suggestions.map((item) => (
            <li key={item.id} className="native-suggest-item">
              <button
                type="button"
                className="native-suggest-pick"
                onMouseDown={(event) => {
                  event.preventDefault();
                  void pick(item);
                }}
              >
                {item.value}
              </button>
              <button
                type="button"
                className="native-suggest-hide"
                title="Скрыть подсказку"
                onMouseDown={(event) => {
                  event.preventDefault();
                  void hide(item);
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export function NativeVerticalForm(props: {
  readonly fields: readonly NativeFormField[];
  readonly documents: readonly NativeFormDocumentRequirement[];
  readonly draft: Record<string, unknown>;
  readonly disabled: boolean;
  readonly api?: NativeBookingApi | null;
  readonly fieldErrors?: Readonly<Record<string, string>>;
  readonly onFieldChange: (fieldKey: string, value: string) => void;
  readonly onFieldsChange?: (patch: Record<string, string>) => void;
  readonly onPlateFormatChange?: (
    fieldKey: string,
    selection: RuPlateRuntimeSelection,
  ) => void;
  readonly onDocumentFieldChange: (
    documentTypeId: string,
    subjectBinding: string,
    fieldCode: string,
    value: string,
  ) => void;
}): ReactElement {
  const applicableFields = useMemo(
    () => props.fields.filter((f) => f.applicable),
    [props.fields],
  );
  const applicableDocs = useMemo(
    () => props.documents.filter((d) => d.applicable),
    [props.documents],
  );
  const plateChoices = readNativePlateFormatChoices(props.draft);

  return (
    <div className="native-flat-fields">
      {applicableFields.map((field) => {
        const invalid = props.fieldErrors?.[field.field_key] !== undefined;
        const fixedFormat = fieldRuPlateFormat(field);
        const selectable = fieldIsRuPlateSelectable(field);
        const runtimeSelection = plateChoices[field.field_key];
        const effectiveFormat: RuPlateFormatCode | null =
          fixedFormat !== null
            ? fixedFormat
            : selectable &&
                runtimeSelection !== undefined &&
                runtimeSelection !== RU_PLATE_RUNTIME_NONE
              ? runtimeSelection
              : null;
        const freeSelectable =
          selectable &&
          (runtimeSelection === undefined || runtimeSelection === RU_PLATE_RUNTIME_NONE);
        const useSuggestions =
          !props.disabled &&
          fieldAcceptsTextSuggestions(field) &&
          props.api !== undefined &&
          props.api !== null;
        return (
          <label key={field.field_key} className="native-field" data-native-field-wrap={field.field_key}>
            {field.label}
            {field.required ? <span className="native-required"> *</span> : null}
            {selectable ? (
              <PlateTypeSelect
                fieldKey={field.field_key}
                value={runtimeSelection ?? ''}
                disabled={props.disabled}
                onChange={(selection) => {
                  props.onPlateFormatChange?.(field.field_key, selection);
                }}
              />
            ) : null}
            {useSuggestions ? (
              <SuggestionField
                field={field}
                value={draftValue(props.draft, field.field_key)}
                disabled={false}
                invalid={invalid}
                api={props.api!}
                allFields={applicableFields}
                ruPlateFormat={effectiveFormat}
                onChange={props.onFieldChange}
                onMultiChange={(patch) => {
                  if (props.onFieldsChange !== undefined) {
                    props.onFieldsChange(patch);
                  } else {
                    for (const [key, value] of Object.entries(patch)) {
                      props.onFieldChange(key, value);
                    }
                  }
                }}
              />
            ) : effectiveFormat !== null ? (
              <RuPlateInput
                format={effectiveFormat}
                value={draftValue(props.draft, field.field_key)}
                disabled={props.disabled}
                readOnly={props.disabled}
                invalid={invalid}
                fieldKey={field.field_key}
                onChange={(value) => {
                  if (!props.disabled) {
                    props.onFieldChange(field.field_key, value);
                  }
                }}
              />
            ) : (
              <input
                className={
                  invalid
                    ? 'native-input is-invalid'
                    : props.disabled
                      ? 'native-input is-readonly'
                      : 'native-input'
                }
                type={inputTypeForValueType(field.value_type)}
                disabled={props.disabled}
                readOnly={props.disabled}
                value={draftValue(props.draft, field.field_key)}
                data-native-field={field.field_key}
                aria-invalid={invalid}
                onChange={(event) => {
                  if (props.disabled) {
                    return;
                  }
                  props.onFieldChange(field.field_key, event.target.value);
                }}
              />
            )}
            {selectable && runtimeSelection === undefined ? (
              <span className="meta" data-testid={`native-plate-type-hint-${field.field_key}`}>
                Выберите тип знака для этого поля. Тип не угадывается по виду техники.
              </span>
            ) : null}
            {selectable && freeSelectable && runtimeSelection === RU_PLATE_RUNTIME_NONE ? (
              <span className="meta">Свободный ввод без маски госномера РФ.</span>
            ) : null}
            {invalid ? (
              <span className="native-field-error" role="alert">
                {props.fieldErrors![field.field_key]}
              </span>
            ) : null}
          </label>
        );
      })}
      {applicableDocs.map((doc) => {
        const identity = documentIdentityKey(doc.document_type_id, doc.subject_binding);
        return (
          <div key={identity} className="native-doc-block">
            <h3 className="native-form-doc-title">
              {doc.display_label}
              <span className="native-muted"> · {doc.subject_binding}</span>
              {doc.required ? <span className="native-required"> *</span> : null}
            </h3>
            <div className="native-flat-fields">
              {doc.metadata_fields.map((meta) => {
                const errKey = `${identity}:${meta.field_code}`;
                const plateSelectionKey = documentMetadataPlateSelectionKey(
                  doc.document_type_id,
                  doc.subject_binding,
                  meta.field_code,
                );
                const invalid =
                  props.fieldErrors?.[errKey] !== undefined ||
                  props.fieldErrors?.[plateSelectionKey] !== undefined ||
                  props.fieldErrors?.[`${identity}.${meta.field_code}`] !== undefined;
                const metaValidation = meta.validation ?? {};
                const metaFixedFormat = fieldRuPlateFormat({ validation: metaValidation });
                const metaSelectable = fieldIsRuPlateSelectable({ validation: metaValidation });
                const metaRuntimeSelection = readDocumentMetaPlateSelection(
                  props.draft,
                  doc.document_type_id,
                  doc.subject_binding,
                  meta.field_code,
                );
                const metaEffectiveFormat: RuPlateFormatCode | null =
                  metaFixedFormat !== null
                    ? metaFixedFormat
                    : metaSelectable &&
                        metaRuntimeSelection !== undefined &&
                        metaRuntimeSelection !== RU_PLATE_RUNTIME_NONE
                      ? metaRuntimeSelection
                      : null;
                const metaFreeSelectable =
                  metaSelectable &&
                  (metaRuntimeSelection === undefined ||
                    metaRuntimeSelection === RU_PLATE_RUNTIME_NONE);
                const metaError =
                  props.fieldErrors?.[errKey] ??
                  props.fieldErrors?.[`${identity}.${meta.field_code}`] ??
                  props.fieldErrors?.[plateSelectionKey];
                return (
                  <label key={errKey} className="native-field" data-native-field-wrap={errKey}>
                    {meta.label}
                    {metaSelectable ? (
                      <PlateTypeSelect
                        fieldKey={plateSelectionKey}
                        value={metaRuntimeSelection ?? ''}
                        disabled={props.disabled}
                        onChange={(selection) => {
                          props.onPlateFormatChange?.(plateSelectionKey, selection);
                        }}
                      />
                    ) : null}
                    {metaEffectiveFormat !== null ? (
                      <RuPlateInput
                        format={metaEffectiveFormat}
                        value={docDraftValue(
                          props.draft,
                          doc.document_type_id,
                          doc.subject_binding,
                          meta.field_code,
                        )}
                        disabled={props.disabled}
                        invalid={invalid}
                        fieldKey={errKey}
                        onChange={(value) => {
                          props.onDocumentFieldChange(
                            doc.document_type_id,
                            doc.subject_binding,
                            meta.field_code,
                            value,
                          );
                        }}
                      />
                    ) : (
                      <input
                        className={invalid ? 'native-input is-invalid' : 'native-input'}
                        type={inputTypeForValueType(meta.value_type)}
                        disabled={props.disabled}
                        value={docDraftValue(
                          props.draft,
                          doc.document_type_id,
                          doc.subject_binding,
                          meta.field_code,
                        )}
                        data-native-field={errKey}
                        aria-invalid={invalid}
                        onChange={(event) => {
                          props.onDocumentFieldChange(
                            doc.document_type_id,
                            doc.subject_binding,
                            meta.field_code,
                            event.target.value,
                          );
                        }}
                      />
                    )}
                    {metaSelectable && metaRuntimeSelection === undefined ? (
                      <span className="meta" data-testid={`native-plate-type-hint-${plateSelectionKey}`}>
                        Выберите тип знака для этого реквизита. Тип не угадывается по виду техники.
                      </span>
                    ) : null}
                    {metaSelectable &&
                    metaFreeSelectable &&
                    metaRuntimeSelection === RU_PLATE_RUNTIME_NONE ? (
                      <span className="meta">Свободный ввод без маски госномера РФ.</span>
                    ) : null}
                    {invalid && metaError !== undefined ? (
                      <span className="native-field-error" role="alert">
                        {metaError}
                      </span>
                    ) : null}
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
